// Backend client for the Rimmon Farm auth/orders API (Lambda + RDS).
// In production the API is proxied same-domain at /api via CloudFront.
// From localhost we call the live API directly (CORS allows localhost).
const API_BASE = (() => {
  if (typeof window !== "undefined" && window.RIMMON_API_URL) {
    return window.RIMMON_API_URL.replace(/\/$/, "");
  }
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  if (host === "rimmonfarm.com" || host === "www.rimmonfarm.com" || host.endsWith(".cloudfront.net")) {
    return `${window.location.origin}/api`;
  }
  // Local dev / preview
  return "https://rimmonfarm.com/api";
})();

const TOKEN_KEY = "rf_token";

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};
export const setToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

async function request(path, { method = "GET", body, auth = false, adminKey } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (adminKey) headers["X-Admin-Key"] = adminKey;

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error("Cannot reach the server. Check your internet connection and try again.");
  }

  const isJson = (res.headers.get("content-type") || "").includes("application/json");
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* non-JSON body — handled below */
  }
  if (!res.ok) {
    throw new Error(data.error || `Request failed (${res.status}).`);
  }
  // A 2xx that isn't JSON means the request never reached the API — e.g. an
  // edge rule serving the SPA shell. Treating that as an empty success would
  // silently swallow the failure, so surface it instead.
  if (!isJson) {
    throw new Error("Unexpected response from the server. Please try again.");
  }
  return data;
}

// A short, human-friendly order number (e.g. #A1B2C3D4) from the internal id.
export function shortOrderId(id) {
  return `#${String(id || "").replace(/-/g, "").slice(0, 8).toUpperCase()}`;
}

// A friendly payment status label + tone for badges.
export function paymentBadge(order) {
  const method = order?.paymentMethod;
  const status = order?.paymentStatus;
  if (String(order?.status).toLowerCase() === "cancelled") {
    if (status === "refunded") return { text: "Cancelled · refunded", tone: "warn" };
    if (status === "refund_pending") return { text: "Cancelled · refund in progress", tone: "warn" };
    return { text: "Cancelled", tone: "warn" };
  }
  if (status === "paid") return { text: "Paid online", tone: "ok" };
  if (method === "online") return { text: "Payment pending", tone: "warn" };
  if (method === "cod") return { text: "Cash on delivery", tone: "info" };
  return { text: "Confirming on WhatsApp", tone: "info" };
}

// Normalize an API order into the shape the UI renders.
export function normalizeOrder(o) {
  return {
    id: o.id,
    date: o.createdAt,
    total: o.totalAmount,
    status: o.status,
    paymentMethod: o.paymentMethod,
    paymentStatus: o.paymentStatus,
    cancelledAt: o.cancelledAt || null,
    cancelReason: o.cancelReason || null,
    cancelledBy: o.cancelledBy || null,
    lines: (o.items || []).map((it) => ({
      id: it.productId || it.id,
      name: it.name,
      qty: it.quantity,
      unit: it.unit,
      lineTotal: it.lineTotal,
    })),
  };
}

export const authApi = {
  requestOtp: (email) => request("/auth/request-otp", { method: "POST", body: { email } }),
  verifyOtp: (email, otp) => request("/auth/verify-otp", { method: "POST", body: { email, otp } }),
  register: (payload) => request("/auth/register", { method: "POST", body: payload }),
  login: (payload) => request("/auth/login", { method: "POST", body: payload }),
  requestLoginOtp: (email) => request("/auth/login/request-otp", { method: "POST", body: { email } }),
  loginWithOtp: (email, otp) => request("/auth/login/verify-otp", { method: "POST", body: { email, otp } }),
  me: () => request("/auth/me", { method: "GET", auth: true }),
  logout: () => request("/auth/logout", { method: "POST", auth: true }),
};

export const ordersApi = {
  place: (payload) => request("/orders", { method: "POST", body: payload, auth: true }),
  mine: () => request("/auth/orders", { method: "GET", auth: true }),
  cancel: (id, reason) =>
    request(`/auth/orders/${id}/cancel`, { method: "POST", body: { reason }, auth: true }),
};

// Statuses a customer can still cancel from — mirrors the API's own gate so the
// button hides instead of failing the request.
const CANCELLABLE = new Set(["pending", "confirmed", "processing"]);
export function canCancelOrder(order) {
  return CANCELLABLE.has(String(order?.status || "pending").toLowerCase());
}

export const paymentsApi = {
  // Create a Razorpay order for an existing internal order (amount taken server-side).
  createOrder: (orderId) => request("/payments/create-order", { method: "POST", body: { orderId }, auth: true }),
  // Verify the Razorpay signature; the order is only marked paid on a match.
  verify: (payload) => request("/payments/verify", { method: "POST", body: payload, auth: true }),
};

// Load the Razorpay Standard Checkout script once, on demand.
let razorpayPromise = null;
export function loadRazorpay() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayPromise) return razorpayPromise;
  razorpayPromise = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      razorpayPromise = null;
      resolve(false);
    };
    document.body.appendChild(script);
  });
  return razorpayPromise;
}

export const addressApi = {
  list: () => request("/auth/addresses", { auth: true }),
  create: (payload) => request("/auth/addresses", { method: "POST", body: payload, auth: true }),
  update: (id, payload) => request(`/auth/addresses/${id}`, { method: "PUT", body: payload, auth: true }),
  remove: (id) => request(`/auth/addresses/${id}`, { method: "DELETE", auth: true }),
};

export const adminApi = {
  stats: (key) => request("/admin/stats", { adminKey: key }),
  users: (key) => request("/admin/users", { adminKey: key }),
  orders: (key) => request("/admin/orders", { adminKey: key }),
  visitors: (key, days = 30) => request(`/admin/visitors?days=${days}`, { adminKey: key }),
  deleteOrder: (key, id) => request(`/admin/orders/${id}`, { method: "DELETE", adminKey: key }),
  cancelOrder: (key, id, reason) =>
    request(`/admin/orders/${id}/cancel`, { method: "POST", body: { reason }, adminKey: key }),
  setOrderStatus: (key, id, status) =>
    request(`/admin/orders/${id}/status`, { method: "PATCH", body: { status }, adminKey: key }),
  clearOrders: (key) => request("/admin/orders", { method: "DELETE", adminKey: key }),
  deleteUser: (key, id) => request(`/admin/users/${id}`, { method: "DELETE", adminKey: key }),
  clearUsers: (key) => request("/admin/users", { method: "DELETE", adminKey: key }),
  setBlocked: (key, id, blocked) =>
    request(`/admin/users/${id}/block`, { method: "PATCH", body: { blocked }, adminKey: key }),
  setCategoryEnabled: (key, categoryId, enabled) =>
    request("/admin/categories", { method: "PATCH", body: { categoryId, enabled }, adminKey: key }),
};

export const trackApi = {
  visit: (payload) => request("/track", { method: "POST", body: payload }),
};

// Public — which category chips the Products page should show. No admin key;
// this just mirrors what an admin has enabled/disabled, not sensitive data.
export const categoriesApi = {
  list: () => request("/categories"),
};
